import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from './useAuth'
import { useProfile } from './useProfile'
import { toast } from 'sonner'
import type { TablesInsert, TablesUpdate } from '@/integrations/supabase/types'
import type { PaymentSetupData, PaymentPlan } from '@/components/projects/PaymentSetupSection'
import { addDays } from 'date-fns'

type ProjectInsert = TablesInsert<'projects'>
type ProjectUpdate = TablesUpdate<'projects'>

interface CreateProjectWithPaymentsInput {
  project: ProjectInsert & { 
    estimated_hours?: number | null
    budget?: number | null 
    fixed_price?: number | null
    billing_type?: string | null
    completion_percentage?: number | null
  }
  paymentSetup?: PaymentSetupData | null
  autoCreatePayment?: boolean // New: auto-create a single payment synced with project
}

interface UpdateProjectWithPaymentsInput {
  id: string
  project: ProjectUpdate & { 
    fixed_price?: number | null
    billing_type?: string | null
    completion_percentage?: number | null
    status?: string | null
  }
  paymentSetup?: PaymentSetupData | null
  syncPayments?: boolean
}

function generatePayments(
  projectId: string,
  userId: string,
  projectValue: number,
  paymentSetup: PaymentSetupData,
  defaultPaymentMethod?: string | null
): TablesInsert<'payments'>[] {
  if (!paymentSetup.createPayments || paymentSetup.paymentPlan === 'custom') {
    return []
  }

  const payments: TablesInsert<'payments'>[] = []
  const { installmentConfig } = paymentSetup
  const paymentMethod = defaultPaymentMethod || 'pix'

  if (paymentSetup.paymentPlan === 'full') {
    // Single payment
    payments.push({
      project_id: projectId,
      user_id: userId,
      amount: projectValue,
      status: 'pending',
      due_date: installmentConfig.firstDueDate || null,
      description: 'Pagamento integral',
      payment_method: paymentMethod,
    })
  } else if (paymentSetup.paymentPlan === 'installments') {
    // Multiple installments
    const { count, intervalDays, firstDueDate } = installmentConfig
    const baseAmount = Math.floor((projectValue * 100) / count) / 100
    const remainder = Math.round((projectValue - baseAmount * count) * 100) / 100

    for (let i = 0; i < count; i++) {
      const amount = i === count - 1 ? baseAmount + remainder : baseAmount
      const dueDate = firstDueDate 
        ? addDays(new Date(firstDueDate), i * intervalDays).toISOString().split('T')[0]
        : null

      payments.push({
        project_id: projectId,
        user_id: userId,
        amount,
        status: 'pending',
        due_date: dueDate,
        description: `Parcela ${i + 1}/${count}`,
        payment_method: paymentMethod,
      })
    }
  }

  return payments
}

// Hook to get user's default payment method
export function useDefaultPaymentMethod() {
  const { user } = useAuth()
  
  return useQuery({
    queryKey: ['profile', 'default-payment-method', user?.id],
    queryFn: async () => {
      if (!user?.id) return 'pix'
      
      const { data, error } = await supabase
        .from('profiles')
        .select('default_payment_method')
        .eq('id', user.id)
        .maybeSingle()
      
      if (error) throw error
      return (data as any)?.default_payment_method || 'pix'
    },
    enabled: !!user?.id,
  })
}

export function useCreateProjectWithPayments() {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const { data: profile } = useProfile()

  return useMutation({
    mutationFn: async ({ project, paymentSetup, autoCreatePayment = true }: CreateProjectWithPaymentsInput) => {
      if (!user) throw new Error('Usuário não autenticado')

      // Get user's default payment method
      const defaultPaymentMethod = (profile as any)?.default_payment_method || 'pix'

      // Create project
      const { data: newProject, error: projectError } = await supabase
        .from('projects')
        .insert(project as ProjectInsert)
        .select()
        .single()

      if (projectError) throw projectError

      // Generate and create payments if configured via paymentSetup
      if (
        paymentSetup &&
        paymentSetup.createPayments &&
        project.billing_type === 'fixed' &&
        project.fixed_price &&
        project.fixed_price > 0
      ) {
        const payments = generatePayments(
          newProject.id,
          user.id,
          project.fixed_price,
          paymentSetup,
          defaultPaymentMethod
        )

        if (payments.length > 0) {
          const { error: paymentsError } = await supabase
            .from('payments')
            .insert(payments)

          if (paymentsError) {
            console.error('Erro ao criar pagamentos:', paymentsError)
            toast.error('Projeto criado, mas houve erro ao criar os pagamentos')
          }
        }
      } 
      // Auto-create a single synced payment if no custom paymentSetup and autoCreatePayment is true
      else if (
        autoCreatePayment && 
        !paymentSetup?.createPayments &&
        project.billing_type === 'fixed' && 
        project.fixed_price && 
        project.fixed_price > 0
      ) {
        const { error: paymentsError } = await supabase
          .from('payments')
          .insert({
            project_id: newProject.id,
            user_id: user.id,
            amount: project.fixed_price,
            status: 'pending',
            due_date: addDays(new Date(), 30).toISOString().split('T')[0],
            description: `Pagamento - ${project.name}`,
            payment_method: defaultPaymentMethod,
          })

        if (paymentsError) {
          console.error('Erro ao criar pagamento sincronizado:', paymentsError)
        }
      }
      // For hourly projects with estimated hours and rate, create a projected payment
      else if (
        autoCreatePayment &&
        project.billing_type === 'hourly' &&
        project.estimated_hours &&
        project.estimated_hours > 0 &&
        (project.hourly_rate || (profile as any)?.hourly_rate)
      ) {
        const rate = project.hourly_rate || (profile as any)?.hourly_rate || 0
        const estimatedValue = project.estimated_hours * rate
        
        if (estimatedValue > 0) {
          const { error: paymentsError } = await supabase
            .from('payments')
            .insert({
              project_id: newProject.id,
              user_id: user.id,
              amount: estimatedValue,
              status: 'pending',
              due_date: addDays(new Date(), 30).toISOString().split('T')[0],
              description: `Pagamento estimado - ${project.name}`,
              payment_method: defaultPaymentMethod,
            })

          if (paymentsError) {
            console.error('Erro ao criar pagamento estimado:', paymentsError)
          }
        }
      }

      return newProject
    },
    onSuccess: (_, { paymentSetup }) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      queryClient.invalidateQueries({ queryKey: ['payments'] })
      
      const hasPayments = paymentSetup?.createPayments && paymentSetup.paymentPlan !== 'custom'
      toast.success(
        hasPayments 
          ? 'Projeto criado com pagamentos configurados!' 
          : 'Projeto criado com pagamento sincronizado!'
      )
    },
    onError: (error: Error) => {
      toast.error(`Erro ao criar projeto: ${error.message}`)
    },
  })
}

export function useUpdateProjectWithPayments() {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const { data: profile } = useProfile()

  return useMutation({
    mutationFn: async ({ id, project, paymentSetup, syncPayments }: UpdateProjectWithPaymentsInput) => {
      if (!user) throw new Error('Usuário não autenticado')

      // Get the previous project data to check if it changed to 'done'
      const { data: previousProject } = await supabase
        .from('projects')
        .select('status, billing_type, fixed_price, hourly_rate, estimated_hours')
        .eq('id', id)
        .single()

      const previousStatus = previousProject?.status
      const newStatus = project.status

      // Update project
      const { data: updatedProject, error: projectError } = await supabase
        .from('projects')
        .update(project)
        .eq('id', id)
        .select()
        .single()

      if (projectError) throw projectError

      // If project was marked as done and wasn't done before
      if (newStatus === 'done' && previousStatus !== 'done') {
        const defaultPaymentMethod = (profile as any)?.default_payment_method || 'pix'
        
        // Check if there are any payments linked to this project
        const { data: existingPayments, error: checkError } = await supabase
          .from('payments')
          .select('id, status')
          .eq('project_id', id)

        if (checkError) {
          console.error('Erro ao verificar pagamentos:', checkError)
        }

        const hasExistingPayments = existingPayments && existingPayments.length > 0

        if (hasExistingPayments) {
          // Mark all pending/overdue payments as paid
          const { error: updatePaymentsError } = await supabase
            .from('payments')
            .update({
              status: 'paid',
              paid_date: new Date().toISOString().split('T')[0],
              payment_method: defaultPaymentMethod,
            })
            .eq('project_id', id)
            .in('status', ['pending', 'overdue'])

          if (updatePaymentsError) {
            console.error('Erro ao marcar pagamentos como pagos:', updatePaymentsError)
            toast.error('Projeto concluído, mas houve erro ao atualizar pagamentos')
          } else {
            toast.success('Projeto concluído e pagamentos marcados como pagos! 🎉')
          }
        } else {
          // No existing payments - create one based on project type
          const billingType = (updatedProject as any).billing_type || previousProject?.billing_type || 'hourly'
          const fixedPrice = (updatedProject as any).fixed_price || previousProject?.fixed_price || 0
          const projectHourlyRate = (updatedProject as any).hourly_rate || previousProject?.hourly_rate || (profile as any)?.hourly_rate || 0

          // For hourly projects, calculate billed amount from time entries
          let paymentAmount = 0
          let paymentDescription = ''

          if (billingType === 'fixed' && fixedPrice > 0) {
            paymentAmount = fixedPrice
            paymentDescription = `Pagamento - ${(updatedProject as any).name}`
          } else if (billingType === 'hourly') {
            // Get billable time entries only
            const { data: timeEntries } = await supabase
              .from('time_entries')
              .select('duration_minutes, hourly_rate, is_billable')
              .eq('project_id', id)

            if (timeEntries && timeEntries.length > 0) {
              // Filter only billable entries and calculate value
              const billableEntries = timeEntries.filter(entry => entry.is_billable !== false)
              
              let billableValue = 0
              let billableMinutes = 0
              
              billableEntries.forEach(entry => {
                const minutes = entry.duration_minutes || 0
                const rate = entry.hourly_rate || projectHourlyRate
                billableMinutes += minutes
                billableValue += (minutes / 60) * rate
              })
              
              const billableHours = billableMinutes / 60
              paymentAmount = Math.round(billableValue * 100) / 100
              paymentDescription = `Pagamento (${Math.round(billableHours * 10) / 10}h faturáveis) - ${(updatedProject as any).name}`
            }
          }

          if (paymentAmount > 0) {
            const { error: createPaymentError } = await supabase
              .from('payments')
              .insert({
                project_id: id,
                user_id: user.id,
                amount: Math.round(paymentAmount * 100) / 100,
                status: 'paid',
                paid_date: new Date().toISOString().split('T')[0],
                description: paymentDescription,
                payment_method: defaultPaymentMethod,
              })

            if (createPaymentError) {
              console.error('Erro ao criar pagamento:', createPaymentError)
              toast.error('Projeto concluído, mas houve erro ao criar pagamento')
            } else {
              toast.success('Projeto concluído e pagamento criado automaticamente! 🎉')
            }
          } else {
            toast.success('Projeto concluído!')
          }
        }
      }

      // If syncPayments is true and we have payment setup, recreate pending payments
      if (
        syncPayments &&
        paymentSetup &&
        paymentSetup.createPayments &&
        project.billing_type === 'fixed' &&
        project.fixed_price &&
        project.fixed_price > 0
      ) {
        const defaultPaymentMethod = (profile as any)?.default_payment_method || 'pix'
        
        // First, delete all pending payments for this project
        const { error: deleteError } = await supabase
          .from('payments')
          .delete()
          .eq('project_id', id)
          .eq('status', 'pending')

        if (deleteError) {
          console.error('Erro ao remover pagamentos pendentes:', deleteError)
        }

        // Then create new payments
        const payments = generatePayments(
          id,
          user.id,
          project.fixed_price,
          paymentSetup,
          defaultPaymentMethod
        )

        if (payments.length > 0) {
          const { error: paymentsError } = await supabase
            .from('payments')
            .insert(payments)

          if (paymentsError) {
            console.error('Erro ao criar novos pagamentos:', paymentsError)
            toast.error('Projeto atualizado, mas houve erro ao sincronizar pagamentos')
          }
        }
      }

      return updatedProject
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: ['projects', variables.id] })
      const previousProject = queryClient.getQueryData(['projects', variables.id])
      
      queryClient.setQueryData(['projects', variables.id], (old: any) => {
        if (!old) return old
        return { ...old, ...variables.project }
      })
      
      return { previousProject }
    },
    onError: (err, variables, context) => {
      if (context?.previousProject) {
        queryClient.setQueryData(['projects', variables.id], context.previousProject)
      }
      toast.error(`Erro ao atualizar projeto: ${err.message}`)
    },
    onSettled: (_, __, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      queryClient.invalidateQueries({ queryKey: ['projects', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['payments'] })
    },
    onSuccess: (_, { syncPayments, project }) => {
      // Only show generic success if we didn't already show a completion message
      if (project.status !== 'done') {
        toast.success(
          syncPayments 
            ? 'Projeto e pagamentos sincronizados!' 
            : 'Projeto atualizado com sucesso!'
        )
      }
    },
  })
}
